import { useCallback, useRef } from "react";
import api from "@/lib/api";
import {
  DEFAULT_LEG,
  clonePeopleFromLeg,
  composeName,
  paxHasContent,
} from "@/lib/newTripConstants";

// Bundles every leg-mutating handler (updateLeg + ETA, add/remove leg,
// passenger CRUD, crew CRUD) into a single hook. Each returned handler is
// memoized with stable identity across renders (deps limited to setters and
// rarely-changing scalars like aircraftId); `legs` mutations go through
// functional `setLegs(prev => ...)` so we never need `legs` itself in the
// deps. This is what lets `React.memo(LegSection)` actually stick.
//
// `updateLeg` debounces the per-leg-per-field ETA fetch by 300ms — typing
// "MMMX" in origin used to fire 4 flight-calc requests; now it fires one.
const ETA_DEBOUNCE_MS = 300;

// PIC and SIC are exclusive (one each per leg); DH/OBS are unbounded. Pure
// helpers (no hook state) so they live at module scope — stable identity, no
// useCallback dep churn.
const COMMAND_ROLES = ["PIC", "SIC"];

// Default flight_role: el primer tripulante es PIC, el segundo SIC, y de ahí en
// adelante OBS — porque PIC y SIC son únicos por leg (un solo capitán y un solo
// segundo al mando). Antes devolvía SIC fijo cuando ya había PIC, lo que creaba
// un 2.º SIC al agregar un 3.er tripulante.
const defaultFlightRoleFor = (leg) => {
  const all = [...(leg.crew || []), ...(leg.external_crew || [])];
  if (!all.some((c) => c.flight_role === "PIC")) return "PIC";
  if (!all.some((c) => c.flight_role === "SIC")) return "SIC";
  return "OBS";
};

// When a crew member is re-assigned to a command role already held by someone
// else, the previous holder is demoted to the role being vacated (a swap) so we
// never end up with two PICs or two SICs. `selfId` is the member being changed,
// excluded from the "already taken" check. Internal crew identify by `user_id`,
// external crew by `id` — match on either.
const applyCommandRoleSwap = (leg, selfId, prevRole, newRole) => {
  if (!COMMAND_ROLES.includes(newRole) || newRole === prevRole) return leg;
  const swap = (c) =>
    (c.user_id || c.id) !== selfId && c.flight_role === newRole
      ? { ...c, flight_role: COMMAND_ROLES.includes(prevRole) ? prevRole : "OBS" }
      : c;
  return {
    ...leg,
    crew: (leg.crew || []).map(swap),
    external_crew: (leg.external_crew || []).map(swap),
  };
};

export default function useLegHandlers({
  legs,
  setLegs,
  expandedLeg,
  setExpandedLeg,
  aircraftId,
  temporaryCrew,
  setExtCrewPickerLeg,
}) {
  // `legs` is read inside addLeg/openExtCrewPicker for paths that don't have
  // a clean functional-setLegs form. Stash in a ref so the handlers stay
  // stable across keystrokes while still reading the latest snapshot.
  const legsRef = useRef(legs);
  legsRef.current = legs;
  const temporaryCrewRef = useRef(temporaryCrew);
  temporaryCrewRef.current = temporaryCrew;
  const expandedLegRef = useRef(expandedLeg);
  expandedLegRef.current = expandedLeg;
  const aircraftIdRef = useRef(aircraftId);
  aircraftIdRef.current = aircraftId;

  // Per-(legIndex,field) ETA debounce timers.
  const etaTimersRef = useRef(new Map());

  const calculateETA = useCallback(
    async (origin, destination, departureDate, departureTime, currentAircraftId) => {
      const empty = {
        arrival_date: "",
        arrival_time: "",
        ete_formatted: "",
        tz_diff_hours: null,
        origin_info: null,
        dest_info: null,
        wind: null,
      };
      if (!origin || !destination || origin.length !== 4 || destination.length !== 4) return empty;
      if (!departureDate || !currentAircraftId) return empty;

      try {
        const depTime = departureTime || "12:00";
        const depDateTime = `${departureDate}T${depTime}`;
        const res = await api.get(
          `/flight-calc?origin=${origin}&destination=${destination}&etd=${depDateTime}&aircraft_id=${currentAircraftId}`
        );
        if (res.data?.ete_formatted) {
          const [arrDate, arrTime] = res.data.eta_local ? res.data.eta_local.split("T") : ["", ""];
          return {
            arrival_date: arrDate || "",
            arrival_time: (arrTime || "").substring(0, 5),
            ete_formatted: res.data.ete_formatted,
            tz_diff_hours: res.data.tz_diff_hours ?? null,
            origin_info: res.data.origin || null,
            dest_info: res.data.destination || null,
            wind: res.data.wind || null,
          };
        }
      } catch (err) {
        console.error("ETA calculation failed:", err);
      }
      return empty;
    },
    []
  );

  const updateLeg = useCallback(
    (index, field, value) => {
      setLegs((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [field]: value };
        // Permissive chaining: editing a destination no longer rewrites the
        // next leg's origin. A leg may start away from the previous landing
        // (intermediate / repositioning leg); origin is a free field, prefilled
        // as a convenience by addLeg but never re-locked here.
        return updated;
      });

      if (!["origin", "destination", "departure_date", "departure_time"].includes(field)) return;

      // Debounce the ETA fetch per (legIndex, field). Keystrokes within
      // 300ms collapse into a single fetch using the latest leg snapshot.
      const key = `${index}:${field}`;
      const timers = etaTimersRef.current;
      if (timers.has(key)) clearTimeout(timers.get(key));
      const handle = setTimeout(async () => {
        timers.delete(key);
        const currentAircraftId = aircraftIdRef.current;
        const leg = legsRef.current[index];
        if (!leg) return;
        if (
          leg.origin &&
          leg.destination &&
          leg.departure_date &&
          !leg.time_tbd &&
          currentAircraftId
        ) {
          const eta = await calculateETA(
            leg.origin,
            leg.destination,
            leg.departure_date,
            leg.departure_time,
            currentAircraftId
          );
          if (eta.ete_formatted || eta.arrival_date) {
            setLegs((prev) => {
              const updated = [...prev];
              updated[index] = { ...updated[index], ...eta };
              return updated;
            });
          }
        }
      }, ETA_DEBOUNCE_MS);
      timers.set(key, handle);
    },
    [setLegs, calculateETA]
  );

  const addLeg = useCallback(() => {
    const currentLegs = legsRef.current;
    const lastLeg = currentLegs[currentLegs.length - 1];
    // El tramo nuevo nace heredando pax+tripulación del anterior (ambos toggles
    // en ON). El usuario puede apagar cada uno desde el banner DENTRO del propio
    // tramo (decisión per-tramo, ya no global) — ver setLegInherit en el modal.
    const cloned = clonePeopleFromLeg(lastLeg, { pax: true, crew: true }) || {};
    const passengers = cloned.passengers?.length
      ? cloned.passengers
      : [
          {
            first_name: "",
            middle_name: "",
            last_name: "",
            passport_number: "",
            passport_expiration: "",
            passport_country: "",
            dob: "",
            address: "",
          },
        ];
    setLegs((prev) => [
      ...prev,
      {
        ...DEFAULT_LEG,
        // Stable client-side id so React.memo + map-by-key survives leg
        // removals from the middle of the list. Stripped by buildLegPayload.
        _client_id: `leg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        // Estado del banner "Copiar del tramo anterior" — vive en el tramo.
        inherit_pax: true,
        inherit_crew: true,
        origin: lastLeg.destination || "",
        ...cloned,
        passengers,
      },
    ]);
    setExpandedLeg(currentLegs.length);
  }, [setLegs, setExpandedLeg]);

  const removeLeg = useCallback(
    (index) => {
      let removed = false;
      setLegs((prev) => {
        if (prev.length <= 1) return prev;
        removed = true;
        // Permissive chaining: removing a leg no longer re-anchors the
        // following legs' origins to the prior destination. The remaining legs
        // keep their own origins (a gap may appear until the coordinator fixes
        // it) — the deliberate, visible inconsistency the permissive model wants.
        return prev.filter((_, i) => i !== index);
      });
      if (!removed) return;
      const curExpanded = expandedLegRef.current;
      if (curExpanded >= index && curExpanded > 0) {
        setExpandedLeg(curExpanded - 1);
      }
    },
    [setLegs, setExpandedLeg]
  );

  const setPassengerCount = useCallback(
    (legIndex, count) => {
      setLegs((prev) => {
        const cur = prev[legIndex].passengers || [];
        const isFerry = prev[legIndex].is_ferry_flight;
        const minCount = isFerry ? 0 : 1;
        const newCount = Math.max(minCount, Math.min(20, count));
        let nextPassengers;
        if (newCount === cur.length) {
          nextPassengers = cur;
        } else if (newCount > cur.length) {
          const blanks = Array.from({ length: newCount - cur.length }, () => ({
            first_name: "",
            middle_name: "",
            last_name: "",
            passport_number: "",
            passport_expiration: "",
            passport_country: "",
            dob: "",
            address: "",
          }));
          nextPassengers = [...cur, ...blanks];
        } else {
          // Lowering the count removes EMPTY rows (from the end), never a row
          // the user already filled — a blind slice(0, newCount) used to drop
          // the LAST passenger even when it held data, so reducing from 3→2
          // after typing 3 names silently erased the third. Filled rows are
          // only removable via their own trash button (removePassenger). If
          // there aren't enough empty rows to satisfy the target, we stop early
          // and keep every filled row (the stepper button is disabled in that
          // case, so this is just a safety floor).
          const toRemove = cur.length - newCount;
          const next = [...cur];
          let removed = 0;
          for (let i = next.length - 1; i >= 0 && removed < toRemove; i--) {
            if (!paxHasContent(next[i])) {
              next.splice(i, 1);
              removed++;
            }
          }
          nextPassengers = next;
        }
        return prev.map((leg, i) =>
          i === legIndex
            ? { ...leg, passenger_count: nextPassengers.length, passengers: nextPassengers }
            : leg
        );
      });
    },
    [setLegs]
  );

  const updatePassenger = useCallback(
    (legIndex, paxIndex, field, value) => {
      setLegs((prev) =>
        prev.map((leg, li) => {
          if (li !== legIndex) return leg;
          return {
            ...leg,
            passengers: leg.passengers.map((p, pi) => {
              if (pi !== paxIndex) return p;
              const next = { ...p, [field]: value };
              if (field === "first_name" || field === "last_name") {
                delete next.passenger_id;
              }
              return next;
            }),
          };
        })
      );
    },
    [setLegs]
  );

  // Remove a passenger ROW entirely: splice it out AND drop passenger_count by
  // one so the counter and the rendered rows stay in sync. The trash button used
  // to only blank the fields, which left a phantom empty row and the count
  // untouched (3 pax → delete one → still showed 3). The remaining passengers
  // now slide up to fill the gap. When removing would drop below the leg minimum
  // (1 for normal flights, 0 for ferry), blank the row in place instead of
  // leaving an under-count leg.
  const removePassenger = useCallback(
    (legIndex, paxIndex) => {
      setLegs((prev) =>
        prev.map((leg, li) => {
          if (li !== legIndex) return leg;
          const cur = leg.passengers || [];
          const minCount = leg.is_ferry_flight ? 0 : 1;
          if (cur.length <= minCount) {
            // Can't go lower — clear the row in place instead of removing it.
            return {
              ...leg,
              passengers: cur.map((p, pi) =>
                pi !== paxIndex
                  ? p
                  : {
                      first_name: "",
                      middle_name: "",
                      last_name: "",
                      passport_number: "",
                      passport_expiration: "",
                      passport_country: "",
                      dob: "",
                      address: "",
                    }
              ),
            };
          }
          const nextPassengers = cur.filter((_, pi) => pi !== paxIndex);
          return { ...leg, passengers: nextPassengers, passenger_count: nextPassengers.length };
        })
      );
    },
    [setLegs]
  );

  const selectSuggestedPassenger = useCallback(
    (legIndex, paxIndex, passenger) => {
      setLegs((prev) => {
        const updated = [...prev];
        const targetLeg = { ...updated[legIndex] };
        const passengers = [...(targetLeg.passengers || [])];
        passengers[paxIndex] = {
          passenger_id: passenger.id,
          first_name: passenger.first_name || "",
          middle_name: passenger.middle_name || "",
          last_name: passenger.last_name || "",
          passport_number: passenger.passport_number || "",
          passport_expiration: passenger.passport_expiry || passenger.passport_expiration || "",
          passport_country: passenger.passport_country || "",
          dob: passenger.date_of_birth || passenger.dob || "",
          address: passenger.address || "",
        };
        targetLeg.passengers = passengers;
        updated[legIndex] = targetLeg;
        return updated;
      });
    },
    [setLegs]
  );

  const toggleCrewMember = useCallback(
    // flightRole optional: when omitted (the normal "tap a pilot from the
    // aircraft roster" path), derive PIC→SIC→OBS from who's already on the leg
    // so the first pilot is PIC and the second SIC. Hardcoding "PIC" here made
    // every assigned pilot a PIC (bug 2026-06-07, N402FB).
    (legIndex, crewMember, flightRole = null) => {
      setLegs((prev) =>
        prev.map((leg, idx) => {
          if (idx !== legIndex) return leg;
          const currentCrew = leg.crew || [];
          const existingIndex = currentCrew.findIndex((c) => c.user_id === crewMember.id);
          if (existingIndex >= 0) {
            return { ...leg, crew: currentCrew.filter((c) => c.user_id !== crewMember.id) };
          }
          return {
            ...leg,
            crew: [
              ...currentCrew,
              {
                user_id: crewMember.id,
                name: crewMember.full_name,
                role: crewMember.role,
                flight_role: flightRole || defaultFlightRoleFor(leg),
                is_external: false,
              },
            ],
          };
        })
      );
    },
    [setLegs]
  );

  const updateCrewFlightRole = useCallback(
    (legIndex, crewUserId, newRole) => {
      setLegs((prev) =>
        prev.map((leg, idx) => {
          if (idx !== legIndex) return leg;
          const prevRole = (leg.crew || []).find((c) => c.user_id === crewUserId)?.flight_role;
          // Swap any existing holder of the command role out of it first, then
          // assign — keeps PIC/SIC unique without rejecting the user's intent.
          const swapped = applyCommandRoleSwap(leg, crewUserId, prevRole, newRole);
          return {
            ...swapped,
            crew: (swapped.crew || []).map((c) =>
              c.user_id === crewUserId ? { ...c, flight_role: newRole } : c
            ),
          };
        })
      );
    },
    [setLegs]
  );

  const addExternalCrew = useCallback(
    (legIndex) => {
      setLegs((prev) =>
        prev.map((leg, idx) => {
          if (idx !== legIndex) return leg;
          const externalCrew = leg.external_crew || [];
          return {
            ...leg,
            external_crew: [
              ...externalCrew,
              {
                id: `ext-${Date.now()}`,
                first_name: "",
                middle_name: "",
                last_name: "",
                name: "",
                phone: "",
                email: "",
                flight_role: defaultFlightRoleFor(leg),
                is_external: true,
              },
            ],
          };
        })
      );
    },
    [setLegs]
  );

  const addExternalCrewFromTemp = useCallback(
    (legIndex, tc) => {
      setLegs((prev) =>
        prev.map((leg, idx) => {
          if (idx !== legIndex) return leg;
          const externalCrew = leg.external_crew || [];
          const k = (tc.name || "").toLowerCase().trim();
          if (k && externalCrew.some((ec) => (ec.name || "").toLowerCase().trim() === k)) {
            return leg;
          }
          return {
            ...leg,
            external_crew: [
              ...externalCrew,
              {
                // Reuse the saved temp-crew's STABLE id so the same person keeps
                // one identity across every leg/trip (vs a fresh ext-<ts> that
                // fragmented one pilot into N ids). Manually-typed externals
                // still get an ext-<ts> placeholder; the backend reconciles it
                // to a stable roster id on save.
                id: tc.id || `ext-${Date.now()}`,
                first_name: tc.first_name || "",
                middle_name: tc.middle_name || "",
                last_name: tc.last_name || "",
                name: tc.name || "",
                phone: tc.phone || "",
                email: tc.email || "",
                flight_role: defaultFlightRoleFor(leg),
                is_external: true,
              },
            ],
          };
        })
      );
    },
    [setLegs]
  );

  const openExtCrewPicker = useCallback(
    (legIndex) => {
      const leg = legsRef.current[legIndex];
      const usedNames = new Set(
        (leg?.external_crew || []).map((ec) => (ec.name || "").toLowerCase().trim()).filter(Boolean)
      );
      const availableTemp = (temporaryCrewRef.current || []).filter(
        (tc) => tc.name && !usedNames.has(tc.name.toLowerCase().trim())
      );
      if (availableTemp.length === 0) {
        addExternalCrew(legIndex);
        return;
      }
      setExtCrewPickerLeg(legIndex);
    },
    [addExternalCrew, setExtCrewPickerLeg]
  );

  const updateExternalCrew = useCallback(
    (legIndex, extIndex, field, value) => {
      setLegs((prev) =>
        prev.map((leg, idx) => {
          if (idx !== legIndex) return leg;
          // Changing a command role (PIC/SIC): swap any current holder out of
          // it first so the role stays unique across the leg.
          let working = leg;
          if (field === "flight_role") {
            const self = (leg.external_crew || [])[extIndex];
            working = applyCommandRoleSwap(leg, self?.id, self?.flight_role, value);
          }
          const externalCrew = [...(working.external_crew || [])];
          const next = { ...externalCrew[extIndex], [field]: value };
          // Keep the combined `name` in sync with its split parts so the
          // payload (and any name-keyed dedup/autocomplete) stays consistent.
          if (field === "first_name" || field === "middle_name" || field === "last_name") {
            next.name = composeName(next.first_name, next.middle_name, next.last_name);
          }
          externalCrew[extIndex] = next;
          return { ...working, external_crew: externalCrew };
        })
      );
    },
    [setLegs]
  );

  // Fill several external-crew fields at once (e.g. picking a temp-crew member
  // from the inline autocomplete) and recompose the display `name` from parts.
  const setExternalCrewFields = useCallback(
    (legIndex, extIndex, fields) => {
      setLegs((prev) =>
        prev.map((leg, idx) => {
          if (idx !== legIndex) return leg;
          const externalCrew = [...(leg.external_crew || [])];
          const next = { ...externalCrew[extIndex], ...fields };
          next.name =
            composeName(next.first_name, next.middle_name, next.last_name) || next.name || "";
          externalCrew[extIndex] = next;
          return { ...leg, external_crew: externalCrew };
        })
      );
    },
    [setLegs]
  );

  const removeExternalCrew = useCallback(
    (legIndex, extIndex) => {
      setLegs((prev) =>
        prev.map((leg, idx) => {
          if (idx !== legIndex) return leg;
          return {
            ...leg,
            external_crew: (leg.external_crew || []).filter((_, i) => i !== extIndex),
          };
        })
      );
    },
    [setLegs]
  );

  // isCrewSelected reads `legs` indirectly via ref so the returned ref is
  // stable; LegSection re-renders when its own `leg` prop changes anyway,
  // so a stale snapshot risk is bounded to the current render of that leg.
  const isCrewSelected = useCallback((legIndex, crewId) => {
    const leg = legsRef.current[legIndex];
    return (leg?.crew || []).some((c) => c.user_id === crewId);
  }, []);

  return {
    calculateETA,
    updateLeg,
    addLeg,
    removeLeg,
    setPassengerCount,
    updatePassenger,
    removePassenger,
    selectSuggestedPassenger,
    toggleCrewMember,
    updateCrewFlightRole,
    addExternalCrew,
    addExternalCrewFromTemp,
    openExtCrewPicker,
    updateExternalCrew,
    setExternalCrewFields,
    removeExternalCrew,
    isCrewSelected,
  };
}
